import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coins, CheckCircle2, XCircle, Loader2, Wand2, ArrowRight, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StatCard, Spinner, Empty } from "@/components/app/common";
import { MediaView } from "@/components/app/Checkout";
import { useAuth } from "@/context/AuthContext";
import { api, num, tl, dt } from "@/lib/api";
import { StatusBadge } from "@/lib/labels";
import { VideoRequestBanner } from "@/components/app/VideoRequestBanner";

export default function Dashboard() {
  const { user, setCredits } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/me/dashboard").then(({ data }) => { setD(data); setCredits(data.credits); });
  }, [setCredits]);
  if (!d) return <Spinner />;
  return (
    <div data-testid="user-dashboard">
      <PageHeader title={`Merhaba ${user.first_name} 👋`} desc="Stüdyonuza hoş geldiniz.">
        <Button asChild className="btn-glow rounded-xl" data-testid="dashboard-create-button"><Link to="/panel/olustur"><Wand2 className="mr-2 h-4 w-4" />İçerik Oluştur</Link></Button>
      </PageHeader>
      <VideoRequestBanner />
      <div className="relative mb-6 overflow-hidden rounded-3xl border border-primary/30 p-7 sm:p-9" data-testid="dashboard-credit-card">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-indigo-600/25 via-violet-600/15 to-fuchsia-600/25" />
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Kalan Kredi</div>
        <div className="mt-2 font-display text-5xl font-extrabold sm:text-6xl" data-testid="dashboard-credit-available">{num(d.credits.available)}</div>
        {d.credits.reserved > 0 && <div className="mt-2 text-sm text-muted-foreground" data-testid="dashboard-credit-reserved">{num(d.credits.reserved)} kredi devam eden üretimler için rezerve</div>}
        <Button asChild variant="outline" className="mt-6 rounded-xl bg-background/50" data-testid="dashboard-buy-button"><Link to="/panel/satin-al"><Coins className="mr-2 h-4 w-4" />Kredi Satın Al</Link></Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard icon={CheckCircle2} label="Başarılı Üretim" value={num(d.completed)} testid="stat-completed" accent="bg-emerald-500/10 text-emerald-500" />
        <StatCard icon={XCircle} label="Başarısız Üretim" value={num(d.failed)} testid="stat-failed" accent="bg-rose-500/10 text-rose-500" />
        <StatCard icon={Loader2} label="Devam Eden" value={num(d.in_progress)} testid="stat-in-progress" />
      </div>
      <div className="mt-10 grid gap-8 lg:grid-cols-3">
        <section className="lg:col-span-2">
          <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-semibold">Son Üretimler</h2><Link to="/panel/galeri" className="flex items-center gap-1 text-sm text-primary">Tümü<ArrowRight className="h-3.5 w-3.5" /></Link></div>
          {d.recent_generations.length === 0 ? <Empty icon={Images} title="Henüz içerik yok" desc="İlk içeriğinizi oluşturarak başlayın." /> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {d.recent_generations.map((g) => (
                <Link to="/panel/galeri" key={g.id} className="glass-card glass-card-hover group overflow-hidden" data-testid={`recent-gen-${g.id}`}>
                  <div className="grid aspect-square place-items-center bg-muted/40">
                    {g.output_file_id ? <MediaView id={g.output_file_id} type={g.output_type} controls={false} className="h-full w-full object-cover" /> : <StatusBadge status={g.status} />}
                  </div>
                  <div className="flex items-center justify-between p-3 text-xs"><span className="font-semibold">{g.label}</span><span className="text-muted-foreground">{g.customer_credit_cost} kr</span></div>
                </Link>
              ))}
            </div>
          )}
        </section>
        <section>
          <h2 className="mb-4 text-lg font-semibold">Son Satın Almalar</h2>
          <div className="space-y-2">
            {d.recent_orders.length === 0 && <p className="text-sm text-muted-foreground">Henüz satın alma yok.</p>}
            {d.recent_orders.map((o) => (
              <Link key={o.id} to={`/siparis/${o.id}`} className="glass-card glass-card-hover flex items-center justify-between p-4 text-sm" data-testid={`recent-order-${o.order_no}`}>
                <div><div className="font-semibold">{o.product_snapshot.name}</div><div className="text-xs text-muted-foreground">{dt(o.created_at)} · {tl(o.final_amount)}</div></div>
                <StatusBadge status={o.status} />
              </Link>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
